import React, { ReactNode } from "react";
import "i18next";
import { Resource, i18n as I18nInstance } from "i18next";
import HttpBackend from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";
import i18n from "i18next";
import { I18nextProvider, initReactI18next } from "react-i18next";

import { getResourcesWithFallbacks } from "./browser-resources";
import { config } from "./config";
import { formatLocale } from "./utils";
import { getNamespaceForBuildType } from "./resources";

let baseI18nInstance: I18nInstance | null = null;
let initPromise: Promise<I18nInstance> | null = null;
const localeInstances = new Map<string, I18nInstance>();

export const configureBrowserI18n = async (resources: Resource, lng: string, namespace: string) => {
  if (!i18n.isInitialized) {
    await i18n
      .use(initReactI18next)
      .use(HttpBackend) // Load translations using http-backend
      .use(LanguageDetector) // Detect user language
      .init(
        config(lng, resources, {
          load: "all",
          debug: false,
          defaultNS: namespace,
          ns: namespace,
          backend: {
            loadPath: `/language/{{lng}}/${namespace}.json`,
            addPath: `/language/{{lng}}/${namespace}.json`,
          },
        }),
      );

    // Expose i18n instance to window for use in wrapPageElement
    if (typeof window !== "undefined") {
      (window as any).i18n = i18n;
    }
  } else {
    await i18n.changeLanguage(lng);
  }

  return i18n;
};

export const initBrowserI18n = async (lng: string, namespace: string): Promise<I18nInstance> => {
  // Return existing promise if initialization is in progress
  if (initPromise) {
    return initPromise;
  }

  // If already initialized, just change language
  if (baseI18nInstance) {
    await baseI18nInstance.changeLanguage(lng);
    return baseI18nInstance;
  }

  // Initialize for the first time
  initPromise = (async () => {
    const resources = await getResourcesWithFallbacks(lng, namespace);
    baseI18nInstance = await configureBrowserI18n(resources, lng, namespace);
    return baseI18nInstance;
  })();

  const instance = await initPromise;
  initPromise = null; // Reset for potential future re-initialization (HMR)
  return instance;
};

// Get or create a cached i18n instance for a specific locale
// This ensures we use the same instance structure for SSR and hydration
const getLocaleInstance = (locale: string): I18nInstance => {
  const formattedLocale = formatLocale(locale);

  // Return cached instance if it exists
  if (localeInstances.has(formattedLocale)) {
    return localeInstances.get(formattedLocale)!;
  }

  // Create a new cloned instance for this locale
  // Note: This clones the configuration and shares the resource store with base instance
  const instance = baseI18nInstance
    ? baseI18nInstance.cloneInstance({ lng: formattedLocale })
    : i18n.cloneInstance({ lng: formattedLocale });

  // Cache and return
  localeInstances.set(formattedLocale, instance);
  return instance;
};

// Pre-create and cache a locale instance (call in onClientEntry)
export const getLocaleInstanceSync = (locale: string): I18nInstance => {
  return getLocaleInstance(locale);
};

export const buildI18nextBrowserProvider = (namespace: string) => {
  const I18nextBrowserProvider = ({ children, locale }: { children: ReactNode; locale?: string }) => {
    if (!locale) {
      throw new Error("locale prop is required for I18nextBrowserProvider");
    }

    const instance = getLocaleInstance(locale);

    return <I18nextProvider i18n={instance}>{children}</I18nextProvider>;
  };

  return I18nextBrowserProvider;
};
